import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useLocation } from 'react-router';
import { ruRU } from '@mui/x-data-grid-pro/locales';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { getIsWaysGroupListLoadingSelector, getModesSelector, getWaysGroupListSelector } from '../../selectors';
import { getWaysGroupListAction, setGroupWayOpenAction } from '../../actions';
import { CustomNoRowsOverlay } from '../../../../components/data-grid/utils';
import { normalizeDataGridModel } from '../../../../utils/normalizeDataGridModel';
import './styles.less';
import { TABLE_ORDER_WAYS_COLUMNS } from '../../constants';
import { WayContextComponent } from '../way-context';

const WaysWrapper = ({
	row,
	onGetWaysGroupList,
	waysGroupList,
	isWaysGroupListLoading,
	getDetailCollectionWayPanelContent,
	onSetGroupWayOpen,
}) => {
	const { search } = useLocation();

	const query = new URLSearchParams(search);

	useEffect(() => {
		onGetWaysGroupList({ row, searchQuery: query.toString() });
		onSetGroupWayOpen(row);
	}, [row.id]);

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('childrenSorts', jsonSorts);

		onGetWaysGroupList({ row, searchQuery: query.toString() });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			let jsonFilters = [];

			jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_ORDER_WAYS_COLUMNS()));

			query.set('childrenFilters', jsonFilters);

			onGetWaysGroupList({ row, searchQuery: query.toString() });
		}
	});

	const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState(new Set());

	const handleDetailPanelExpandedRowIdsChange = useCallback(newIds => {
		if (newIds.size > 1) {
			const newSet = [];

			const newIdsArray = [...newIds];
			newSet.push(newIdsArray[newIdsArray.length - 1]);
			setDetailPanelExpandedRowIds(new Set(newSet));
		} else {
			setDetailPanelExpandedRowIds(newIds);
		}
	}, []);

	const [selectedRow, setSelectedRow] = useState(null);

	const [contextMenu, setContextMenu] = useState(null);

	const handleContextMenu = event => {
		event.preventDefault();

		const rowElement = event.target.closest('.MuiDataGrid-row');

		if (rowElement) {
			const rowId = rowElement.getAttribute('data-id');

			const row = waysGroupList.find(r => `${r.id}` === `${rowId}`);

			setSelectedRow(row);
			setContextMenu({
				mouseX: event.clientX + 2,
				mouseY: event.clientY - 6,
			});
		}
	};

	const handleClose = () => {
		setContextMenu(null);
	};

	const contextComponent = (
		<WayContextComponent contextMenu={contextMenu} setContextMenu={setContextMenu} way={selectedRow} />
	);

	const [columnModel, setColumnModel] = useState({});

	const handleColumnWidthChange = params => {
		setColumnModel(prev => ({
			...prev,
			[params.colDef.field]: params.width,
		}));
	};

	const columnsWithSavedWidth = TABLE_ORDER_WAYS_COLUMNS().map(col => ({
		...col,
		width: columnModel[col.field] || col.width,
		flex: columnModel[col.field] ? 0 : col.flex,
	}));

	return (
		<>
			<DataGridPro
				key={`waysGroup - ${row.id}`}
				slots={{
					noRowsOverlay: CustomNoRowsOverlay,
				}}
				slotProps={{
					loadingOverlay: {
						variant: 'skeleton',
						noRowsVariant: 'skeleton',
					},
					row: {
						onContextMenu: handleContextMenu,
						style: { cursor: 'context-menu' },
					},
				}}
				componentsProps={{
					root: {
						onContextMenu: e => {
							e.preventDefault();
							handleClose();
						},
					},
				}}
				initialState={{
					pinnedColumns: { left: ['empty', '__check__', 'actions', '__detail_panel_toggle__', 'id'] },
				}}
				rows={waysGroupList ?? []}
				columns={columnsWithSavedWidth}
				onColumnWidthChange={handleColumnWidthChange}
				hideFooterPagination
				hideFooter
				rowHeight={40}
				columnHeaderHeight={40}
				loading={Boolean(isWaysGroupListLoading.find(el => el === row.id))}
				localeText={ruRU.components.MuiDataGrid.defaultProps.localeText}
				className="no-border"
				sortingMode="server"
				onSortModelChange={handleSortChange}
				filterMode="server"
				filterDebounceMs={300}
				onFilterModelChange={handleFilterChange}
				getDetailPanelContent={getDetailCollectionWayPanelContent}
				detailPanelExpandedRowIds={detailPanelExpandedRowIds}
				onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
				disableMultipleColumnsSorting
			/>

			{contextComponent}
		</>
	);
};

const mapStateToProps = createStructuredSelector({
	waysGroupList: getWaysGroupListSelector(),
	isWaysGroupListLoading: getIsWaysGroupListLoadingSelector(),
	modes: getModesSelector(),
});

const mapDispatchToProps = {
	onGetWaysGroupList: getWaysGroupListAction,
	onSetGroupWayOpen: setGroupWayOpenAction,
};

export const WaysComponent = connect(mapStateToProps, mapDispatchToProps)(WaysWrapper);

WaysWrapper.propTypes = {
	row: PropTypes.object.isRequired,
	onGetWaysGroupList: PropTypes.func,
	waysGroupList: PropTypes.array,
	isWaysGroupListLoading: PropTypes.array.isRequired,
	onResetState: PropTypes.func,
	modes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
		}),
	),
	getDetailCollectionWayPanelContent: PropTypes.func.isRequired,
	onSetGroupWayOpen: PropTypes.func.isRequired,
};
